import Konva from 'konva';
import { useCallback, useRef, useState } from 'react';
import { useMapCanvasShapes } from '../../contexts/MapCanvasContext';
import { useMapEditorTool } from '../../contexts/MapEditorContext';
import { MapEditorTool } from '../../types';
import { getAllSpots, getShapeUnderPoint, getSpotUnderPoint } from './helpers';

interface UseSpotDrawingProps {
  onDrawEnd: (
    hitSpots: { id: string; order: number }[],
    activeTool: MapEditorTool
  ) => void;
  stagePosition: { x: number; y: number; scale: number };
}

interface UseSpotDrawingReturn {
  isDrawing: boolean;
  points: number[];
  handlers: {
    onMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onMouseMove: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onMouseUp: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  };
}

export const useSpotDrawing = ({
  onDrawEnd,
  stagePosition
}: UseSpotDrawingProps): UseSpotDrawingReturn => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState<number[]>([]);
  const hitSpots = useRef<{ id: string; order: number }[]>([]);
  const { activeTool } = useMapEditorTool();
  const { shapes } = useMapCanvasShapes();

  const transformPoint = useCallback(
    (point: { x: number; y: number }) => {
      return {
        x: (point.x - stagePosition.x) / stagePosition.scale,
        y: (point.y - stagePosition.y) / stagePosition.scale
      };
    },
    [stagePosition]
  );

  const handleMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      const point = transformPoint(
        stage?.getPointerPosition() || { x: 0, y: 0 }
      );
      setIsDrawing(true);
      setPoints([point.x, point.y]);
      hitSpots.current = [];
    },
    [transformPoint]
  );

  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (!isDrawing) return;

      const stage = e.target.getStage();
      const stagePointerPosition = e.target
        .getStage()
        ?.getPointerPosition() || { x: 0, y: 0 };
      const point = transformPoint(stagePointerPosition);

      setPoints((prev) => [...prev, point.x, point.y]);

      // Get all spots including those in groups
      const allSpots = getAllSpots(shapes);

      if (activeTool === 'draw-select') {
        // Check for spot intersection
        const shapeId = getShapeUnderPoint(stage, stagePointerPosition);
        if (shapeId) {
          const shape = shapes.find((s) => s.id === shapeId && !s.locked);
          if (shape && !hitSpots.current.find((hit) => hit.id === shapeId)) {
            hitSpots.current.push({
              id: shapeId,
              order: hitSpots.current.length + 1
            });
          }
        }
      } else {
        // Check for spot intersection
        const spotId = getSpotUnderPoint(stage, stagePointerPosition);
        if (spotId) {
          const spot = allSpots.find((s) => s.id === spotId);

          if (spot && !hitSpots.current.find((hit) => hit.id === spotId)) {
            hitSpots.current.push({
              id: spotId,
              order: hitSpots.current.length + 1
            });
          }
        }
      }
    },
    [isDrawing, transformPoint, shapes]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return;

    setIsDrawing(false);
    setPoints([]);
    onDrawEnd(hitSpots.current, activeTool);
    hitSpots.current = [];
  }, [isDrawing, onDrawEnd]);

  return {
    isDrawing,
    points,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp
    }
  };
};
